// What the frame shows where (spec E2, "The canvas"): the scene reports each clip it drew as a
// turned box in the frame's pixels, and a pointer on the canvas picks the top-most box under
// it. Pure geometry: no Pixi, no DOM, so the scene stays deterministic and a test reads it.

/** A clip as drawn: its box's centre and size in the frame's pixels, turned `angle` degrees. */
export interface Drawn {
  clipId: string;
  /** A caption (#239) is picked as a text, but opens no editor on a double-click. */
  kind: "picture" | "text" | "caption";
  cx: number;
  cy: number;
  width: number;
  height: number;
  /** Clockwise, about the centre. */
  angle: number;
}

/** Whether a point (frame pixels) lies in a drawn box, its turn undone first. */
export function contains(box: Drawn, x: number, y: number): boolean {
  const turn = (-box.angle * Math.PI) / 180;
  const dx = x - box.cx;
  const dy = y - box.cy;
  const ux = dx * Math.cos(turn) - dy * Math.sin(turn);
  const uy = dx * Math.sin(turn) + dy * Math.cos(turn);
  return Math.abs(ux) <= box.width / 2 && Math.abs(uy) <= box.height / 2;
}

/** The clip under a point: the last drawn box that holds it (the top one), or null. */
export function pick(drawn: readonly Drawn[], x: number, y: number): string | null {
  for (let at = drawn.length - 1; at >= 0; at -= 1) {
    const box = drawn[at]!;
    if (contains(box, x, y)) return box.clipId;
  }
  return null;
}

/**
 * A pointer's place on the page as the frame's pixels: `rect` is where the frame is drawn on
 * the page (its canvas's box), `size` the frame's own pixels.
 */
export function toFrame(
  rect: { left: number; top: number; width: number; height: number },
  size: { width: number; height: number },
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  return {
    x: ((clientX - rect.left) / Math.max(1, rect.width)) * size.width,
    y: ((clientY - rect.top) / Math.max(1, rect.height)) * size.height,
  };
}

/** A drawn box as a share of the frame: its place and size for the page to outline it. */
export function shareOf(box: Drawn, size: { width: number; height: number }) {
  return {
    left: (box.cx - box.width / 2) / size.width,
    top: (box.cy - box.height / 2) / size.height,
    width: box.width / size.width,
    height: box.height / size.height,
  };
}

/** Whether two frames' boxes are the same (the page redraws its outline only when they differ). */
export function sameDrawn(one: readonly Drawn[], other: readonly Drawn[]): boolean {
  return (
    one.length === other.length &&
    one.every((box, at) => {
      const next = other[at]!;
      return (
        box.clipId === next.clipId &&
        box.cx === next.cx &&
        box.cy === next.cy &&
        box.width === next.width &&
        box.height === next.height &&
        box.angle === next.angle
      );
    })
  );
}
